/**
 * API client for event subscriptions (via pro-api to user-service)
 */

import { authenticatedProApiFetch } from './pro-api.client';

export type MinSeverity = 'all' | 'low' | 'medium' | 'high' | 'critical';

export type AlertOverrideMode = 'allow' | 'block';

export interface AlertCodeOverride {
  code: string;
  mode: AlertOverrideMode;
}

export interface ProductAlertPrefs {
  enabledSeverities: number[];
  overrides: AlertCodeOverride[];
}

export interface EventSubscriptionRow {
  id: string;
  subscriberUserId: string;
  entityType: string;
  entityId: string;
  systemEventKey: string;
  eventLabel: string;
  eventDescription?: string | null;
  summary: string;
  usesSeverity?: boolean;
  notifyEmail: boolean;
  notifySms: boolean;
  notifyWeb: boolean;
  notifyPush: boolean;
  minSeverity: MinSeverity;
  createdAt: string;
  updatedAt: string;
}

export interface SubscriptionsListResponse {
  subscriptions: EventSubscriptionRow[];
  alertPrefs: ProductAlertPrefs | null;
}

export interface ProductSubscriberRow {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  avatarUrl?: string | null;
  eventCount: number;
}

export interface UserSubscriptionGroup {
  entityType: string;
  entityId: string;
  eventCount: number;
  systemEventKeys: string[];
  eventLabels: string[];
}

export interface SubscriptionBatchItem {
  systemEventKey: string;
  notifyEmail: boolean;
  notifySms: boolean;
  notifyWeb: boolean;
  notifyPush: boolean;
  minSeverity: MinSeverity;
}

export async function fetchSubscriptions(
  token: string,
  params: {
    subscriberUserId?: string;
    entityType?: string;
    entityId?: string;
    entityLabel?: string;
    subscriberLabel?: string;
  },
): Promise<SubscriptionsListResponse> {
  const qs = new URLSearchParams();
  if (params.subscriberUserId) qs.set('subscriberUserId', params.subscriberUserId);
  if (params.entityType) qs.set('entityType', params.entityType);
  if (params.entityId) qs.set('entityId', params.entityId);
  if (params.entityLabel) qs.set('entityLabel', params.entityLabel);
  if (params.subscriberLabel) qs.set('subscriberLabel', params.subscriberLabel);
  return authenticatedProApiFetch<SubscriptionsListResponse>(
    `/subscriptions${qs.toString() ? `?${qs.toString()}` : ''}`,
    token,
  );
}

export async function saveSubscriptionsBatch(
  token: string,
  data: {
    subscriberUserId: string;
    entityType: string;
    entityId: string;
    owningOrganizationId?: string | null;
    entityLabel?: string;
    subscriptions: SubscriptionBatchItem[];
    alertPrefs?: ProductAlertPrefs | null;
  },
): Promise<SubscriptionsListResponse> {
  return authenticatedProApiFetch<SubscriptionsListResponse>('/subscriptions/batch', token, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function checkSubscriptionProductAccess(
  token: string,
  params: {
    subscriberUserId: string;
    entityId: string;
    entityType?: string;
    owningOrganizationId?: string | null;
  },
): Promise<{ hasAccess: boolean }> {
  const qs = new URLSearchParams();
  qs.set('subscriberUserId', params.subscriberUserId);
  qs.set('entityId', params.entityId);
  qs.set('entityType', params.entityType ?? 'product');
  if (params.owningOrganizationId) {
    qs.set('owningOrganizationId', params.owningOrganizationId);
  }
  return authenticatedProApiFetch<{ hasAccess: boolean }>(
    `/subscriptions/access?${qs.toString()}`,
    token,
  );
}

export async function fetchProductSubscribers(
  token: string,
  productId: string,
): Promise<ProductSubscriberRow[]> {
  const qs = new URLSearchParams({
    entityType: 'product',
    entityId: productId,
  });
  return authenticatedProApiFetch<ProductSubscriberRow[]>(
    `/subscriptions/subscribers?${qs.toString()}`,
    token,
  );
}

export async function fetchUserSubscriptions(
  token: string,
  userId: string,
): Promise<UserSubscriptionGroup[]> {
  return authenticatedProApiFetch<UserSubscriptionGroup[]>(
    `/subscriptions/for-user/${userId}`,
    token,
  );
}
